import React, { useState, useEffect, useMemo } from 'react';
import { Filter, X, Check } from 'lucide-react';
import moment from 'moment';
import { DASHBOARD_DATE_OPTIONS } from './dashboardManager';
import ListDropdownFilter from '../../components/ListDropdownFilter/ListDropdownFilter';
import TeamDropdownFilter from '../../components/TeamDropdownFilter/TeamDropdownFilter';
import TeamMemberDropdownFilter from '../../components/TeamMemberDropdownFilter/TeamMemberDropdownFilter';
import PopupComponent from '../../components/PopupComponent';
import logger from '../../utils/logger';

export interface FilterValues {
  dateRange: string;
  customFromDate?: string;
  customToDate?: string;
  teamOrOrg: string; // team ID or "organization"
  teamMember: string | string[]; // Support both single and multiple
  listIds: string[];
}

interface DashboardFilterPanelProps {
  // Current applied filter values
  currentFilters: FilterValues;

  // Data for dropdowns
  teamData: any[];
  organizationData: any[];
  userData: any[];

  // Callbacks
  onApplyFilters: (filters: FilterValues) => void;
  onClearFilters: () => void;

  // Helper text
  getDisplayText: () => string;
}

const DashboardFilterPanel: React.FC<DashboardFilterPanelProps> = ({
  currentFilters,
  teamData,
  organizationData,
  userData,
  onApplyFilters,
  onClearFilters,
  getDisplayText
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Pending filters (not yet applied)
  const [pendingFilters, setPendingFilters] = useState<FilterValues>(currentFilters);

  // Update pending filters when currentFilters change from outside
  useEffect(() => {
    setPendingFilters(currentFilters);
  }, [currentFilters]);

  // Check if custom date is selected
  const isCustomDate = pendingFilters.dateRange === 'custom';

  // Check if filters have changed
  const hasChanges = useMemo(() => {
    return JSON.stringify(pendingFilters) !== JSON.stringify(currentFilters);
  }, [pendingFilters, currentFilters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (currentFilters.dateRange && currentFilters.dateRange !== '7d') count++;
    if (currentFilters.teamOrOrg) count++;
    const teamMemberArray = Array.isArray(currentFilters.teamMember)
      ? currentFilters.teamMember
      : (currentFilters.teamMember ? [currentFilters.teamMember] : []);
    if (teamMemberArray.length > 0) count++;
    if (currentFilters.listIds.length > 0) count++;
    return count;
  }, [currentFilters]);

  const handleDateRangeChange = (value: string) => {
    setPendingFilters(prev => ({
      ...prev,
      dateRange: value,
      customFromDate: value === 'custom' ? prev.customFromDate : undefined,
      customToDate: value === 'custom' ? prev.customToDate : undefined
    }));
  };

  const handleCustomDateChange = (name: 'from' | 'to', value: string) => {
    // User picks date in local time (YYYY-MM-DD)
    // We need to create UTC dates at start/end of day
    let isoValue = '';
    if (value) {
      // Parse the date string as YYYY-MM-DD and create UTC moment
      const utcDate = moment.utc(value, 'YYYY-MM-DD');

      if (name === 'from') {
        utcDate.startOf('day'); // 00:00:00 UTC
      } else {
        utcDate.endOf('day'); // 23:59:59.999 UTC
      }

      isoValue = utcDate.format(); // ISO 8601 format
    }

    setPendingFilters(prev => ({
      ...prev,
      [name === 'from' ? 'customFromDate' : 'customToDate']: isoValue
    }));
  };

  const handleTeamOrgChange = (value: string) => {
    setPendingFilters(prev => ({
      ...prev,
      teamOrOrg: value,
      teamMember: [] // Clear team member when changing team/org
    }));
  };

  const handleTeamMemberChange = (value: string | string[]) => {
    setPendingFilters(prev => ({
      ...prev,
      teamMember: value
    }));
  };

  const handleListSelection = (listIds: string[]) => {
    setPendingFilters(prev => ({
      ...prev,
      listIds
    }));
  };

  const handleApply = () => {
    logger.log('🎯 Applying filters:', pendingFilters);
    onApplyFilters(pendingFilters);
    setIsOpen(false);
  };

  const handleClear = () => {
    logger.log('🧹 Clearing all filters');
    const clearedFilters: FilterValues = {
      dateRange: '7d',
      customFromDate: undefined,
      customToDate: undefined,
      teamOrOrg: '',
      teamMember: [],
      listIds: []
    };
    setPendingFilters(clearedFilters);
    onClearFilters();
    setIsOpen(false);
  };

  const handleCancel = () => {
    // Revert pending filters to current applied filters
    setPendingFilters(currentFilters);
    setIsOpen(false);
  };

  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Date Range Dropdown */}
      <select
        value={currentFilters.dateRange}
        onChange={(e) => {
          const newValue = e.target.value;
          handleDateRangeChange(newValue);
          if (newValue !== 'custom') {
            onApplyFilters({ ...currentFilters, dateRange: newValue });
          }
        }}
        style={{
          padding: '8px 12px',
          backgroundColor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#334155',
          cursor: 'pointer',
          minWidth: '180px'
        }}
      >
        {DASHBOARD_DATE_OPTIONS.map((item, index: number) => (
          <option key={`date-${index}`} value={item.value}>
            {item.name}
          </option>
        ))}
      </select>

      {/* Custom Date Inputs */}
      {isCustomDate && (
        <>
          <input
            type="date"
            value={pendingFilters.customFromDate ? pendingFilters.customFromDate.split('T')[0] : ''}
            onChange={(e) => handleCustomDateChange('from', e.target.value)}
            style={{
              padding: '8px 12px',
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#334155',
              cursor: 'pointer'
            }}
            placeholder="From Date"
          />
          <input
            type="date"
            value={pendingFilters.customToDate ? pendingFilters.customToDate.split('T')[0] : ''}
            onChange={(e) => handleCustomDateChange('to', e.target.value)}
            style={{
              padding: '8px 12px',
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#334155',
              cursor: 'pointer'
            }}
            placeholder="To Date"
          />
          <button
            onClick={() => {
              if (pendingFilters.customFromDate && pendingFilters.customToDate) {
                onApplyFilters(pendingFilters);
              }
            }}
            disabled={!pendingFilters.customFromDate || !pendingFilters.customToDate}
            style={{
              padding: '8px 16px',
              backgroundColor: pendingFilters.customFromDate && pendingFilters.customToDate ? '#3b82f6' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: pendingFilters.customFromDate && pendingFilters.customToDate ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Check size={16} />
            Apply
          </button>
        </>
      )}

      {/* Team/Organization Filter */}
      <div style={{ minWidth: '200px' }}>
        <TeamDropdownFilter
          teams={teamData}
          organizations={organizationData}
          selectedValue={currentFilters.teamOrOrg}
          onSelectionChange={(value) => {
            onApplyFilters({ ...currentFilters, teamOrOrg: value, teamMember: '' });
          }}
          placeholder={getDisplayText()}
          style={{ width: '100%' }}
        />
      </div>

      {/* Team Member Filter */}
      <div style={{ minWidth: '200px' }}>
        <TeamMemberDropdownFilter
          members={userData}
          selectedValue={currentFilters.teamMember}
          onSelectionChange={(value) => {
            onApplyFilters({ ...currentFilters, teamMember: value });
          }}
          placeholder="All Team Members"
          style={{ width: '100%' }}
        />
      </div>

      {/* Clear All Button */}
      {activeFilterCount > 0 && (
        <button
          onClick={onClearFilters}
          style={{
            padding: '8px 16px',
            backgroundColor: 'white',
            color: '#6b7280',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <X size={16} />
          Clear All
        </button>
      )}
    </div>
  );
};

export default DashboardFilterPanel;
